import { isBefore, parseISO } from 'date-fns';

import {
  LicenseDemoPeriodModel,
  LicenseModel,
  LicenseType,
} from '../../../../typings/api/skymap/rest/v0/.common';
import { projectStateAdapter } from '../../react/state/project-state/project-state-adapter';
import { userStateAdapter } from '../../react/state/user-state/user-state-adapter';
import { ThemeManager } from '../../react/themes/theme-manager';
import { isIframedByExactStudio } from '../../react/utils/iframe';
import { CerbosFeature } from '../../services/cerbos/features';
import { assertNever, isDefined } from '../variables';

const routesExcludedFromLicenseCheck = [
  '/projects',
  '/users',
  '/newcompanyadmin',
  '/companies',
  '/companies/:companyId',
  '/companies/:companyId/licenses',
  '/companies/:companyId/admins',
  '/registercompany',
  '/verify',
  '/profile',
  '/register-design',
];

/**
 * A list of routes only accessible to users with a valid Design license.
 */
const validRoutesForDesignLicense = [
  '/projects/:projectId',
  '/projects/:projectId/email',
  '/projects/:projectId/filemanager',
  '/projects/:projectId/users',
  '/projects/:projectId/settings',
  '/projects/:projectId/viewer',
  '/companies',
  '/companies/:companyId',
  '/companies/:companyId/licenses',
  '/companies/:companyId/admins',
];

/**
 * Returns a value indicating whether the current user has a license that allows access to the
 * specified view for the current project.
 */
const hasAccessToViewByUserLicenses = (view: string) => {
  if (
    userStateAdapter.getSnapshot().isOrganizationAdmin ||
    routesExcludedFromLicenseCheck.includes(view)
  ) {
    return true;
  }

  const companyId = projectStateAdapter.getSnapshot().project?.companyId;

  return (
    // We don't use findUserLicensesByType() here as that function ignores all global licenses for
    // attendees. Attendees need the license only to check whether access is given to the user for a
    // specific view.
    userStateAdapter
      .getSnapshot()
      .licenses.some(
        (license) =>
          license.companyId === companyId &&
          licensePermissionChecks.isViewAllowedByLicense(view, license),
      )
  );
};

/**
 * Returns a value indicating whether current user has a license that allows access to the specified
 * view.
 */
const isViewAllowedByLicense = (view: string, license: LicenseModel) => {
  const hasActiveSubscription = isDefined(license.startedSubscriptionAt);
  const hasDemoPeriods = (license.licenseDemoPeriods ?? []).length > 0;
  const hasValidDemoPeriod = license.licenseDemoPeriods?.some(isDemoPeriodValid) ?? false;

  if (hasActiveSubscription || !hasDemoPeriods || hasValidDemoPeriod) {
    switch (license.licenseType) {
      case 'DESIGN': {
        return validRoutesForDesignLicense.includes(view);
      }

      case 'PORTAL':
        // This license has access to all views.
        return true;

      default:
        assertNever(license.licenseType);
    }
  }

  return false;
};

/**
 * Returns a value indicating whether the current user has a license that allows access to the
 * specified feature.
 */
const hasAccessToFeatureByUserLicenses = (feature: CerbosFeature) => {
  switch (feature) {
    case 'feature:viewer:chips:as_built':
      return licensePermissionChecks.hasValidLicense('PORTAL');
    default:
      return true;
  }
};

/**
 * Return a list of licenses matching the specified license type.
 *
 * For attendees, the license must be explicitly created for the user. Attendees need a
 * company-only license in order to display menu items, but attendees should not be able to use the
 * tools. That's why we in this function only return licenses for attendees that matches the user ID
 * in the license.
 */
const findUserLicensesByType = (licenseType: LicenseType) => {
  const companyId = projectStateAdapter.getSnapshot().project?.companyId;
  return userStateAdapter.getSnapshot().licenses.filter((license) => {
    if (license.licenseType !== licenseType || license.companyId !== companyId) {
      return false;
    }

    const snapshot = userStateAdapter.getSnapshot();
    if (snapshot.isActiveRoleProjectUser() && license.userId !== snapshot.getActiveUser()?.id) {
      return false;
    }

    return true;
  });
};

/**
 * Returns a value indicating whether the current user has the specified license type for the
 * company that the current project belongs to, and that the license is valid for the current date.
 */
const hasValidLicense = (licenseType: LicenseType) => {
  // MOBA company administrators always have a valid Design license. No Design license exist in the
  // database for MOBA users.
  //
  // TODO: Maybe the backend should be responsible to setup a license for each company admin. Then
  // TODO: this check can be removed.
  if (
    licenseType === 'DESIGN' &&
    ThemeManager.instance.isMobaTheme() &&
    userStateAdapter.getSnapshot().isCompanyAdmin
  ) {
    return true;
  }

  if (licenseType === 'DESIGN' && isIframedByExactStudio()) {
    return true;
  }

  return licensePermissionChecks
    .findUserLicensesByType(licenseType)
    .some(licensePermissionChecks.isLicenseValid);
};

/**
 * Returns a value indicating whether the current user has a specified license type for the
 * company that the current project belongs to, and that the license has expired.
 */
const hasExpiredLicense = (licenseType: LicenseType) => {
  return isDefined(licensePermissionChecks.getExpiredLicense(licenseType));
};

/**
 * Returns the first expired license of the specified type, or undefined if none is found.
 */
const getExpiredLicense = (licenseType: LicenseType) => {
  return licensePermissionChecks
    .findUserLicensesByType(licenseType)
    .find((x) => !licensePermissionChecks.isLicenseValid(x));
};

/**
 * Returns a value indicating whether the specified demo period is valid (has not expired).
 */
const isDemoPeriodValid = (demoPeriod: LicenseDemoPeriodModel) =>
  isBefore(new Date(), parseISO(demoPeriod.validUntil));

/**
 * Returns true if subscription is started or if no demo periods exist. Otherwise true is returned
 * if at least one demo period is valid.
 */
const isLicenseValid = (license: LicenseModel) => {
  if (isDefined(license.startedSubscriptionAt)) {
    return true;
  }

  return isDefined(license.licenseDemoPeriods) && license.licenseDemoPeriods.length > 0
    ? license.licenseDemoPeriods.some(licensePermissionChecks.isDemoPeriodValid)
    : true;
};

export const licensePermissionChecks = {
  findUserLicensesByType,
  getExpiredLicense,
  hasAccessToFeatureByUserLicenses,
  hasAccessToViewByUserLicenses,
  hasExpiredLicense,
  hasValidLicense,
  isDemoPeriodValid,
  isLicenseValid,
  isViewAllowedByLicense,
};
